import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled as muiStyled,
  css,
} from '@mui/material';
import React from 'react';

import { DesignToken } from '~/client/components/base/core';
import { spc } from '~/client/components/base/spacer';

export interface TableColumn {
  key: string;
  /**
   * Header content. Usually a plain string, but may be a ReactNode so callers
   * can render sanitized author rich text (see RichText). The header cell just
   * renders `{column.label}`, and nothing reads it back as a string.
   */
  label: string | React.ReactNode;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
}

export interface TableRowData {
  [key: string]: React.ReactNode;
}

export interface GenericTableProps {
  columns: TableColumn[];
  data: TableRowData[];
  className?: string;
  /**
   * Custom CSS class for the table container
   */
  containerClassName?: string;
  /**
   * Whether to show table headers
   */
  showHeaders?: boolean;
  /**
   * Custom header styling class
   */
  headerClassName?: string;
  /**
   * Custom cell styling class
   */
  cellClassName?: string;
  /**
   * Whether to use Paper component as container
   */
  usePaper?: boolean;
}

const GenericTableImpl: React.FC<GenericTableProps> = ({
  columns,
  data,
  className,
  containerClassName,
  showHeaders = true,
  headerClassName,
  cellClassName,
  usePaper = true,
}) => {
  const Container = usePaper ? Paper : 'div';
  const containerProps = usePaper ? { component: Paper } : {};

  return (
    <TableContainer
      {...containerProps}
      className={`${containerClassName ? containerClassName : ''} generic-table-container`}
    >
      <Table className={className}>
        {showHeaders && (
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={`${headerClassName ? headerClassName : ''} generic-table-header`}
                  style={{
                    width: column.width,
                    textAlign: column.align ?? 'left',
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {data.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={`${cellClassName ? cellClassName : ''} generic-table-cell`}
                  style={{
                    width: column.width,
                    textAlign: column.align ?? 'left',
                  }}
                >
                  {row[column.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const GenericTable = muiStyled(GenericTableImpl)(
  ({ theme }) => css`
    /* Generic table container styles */
    .generic-table-container {
      margin: ${spc.s}px 0;
      overflow: hidden;
      border-radius: 4px;
      box-shadow: ${DesignToken.shadowSoft};
    }

    /* Table base styles - scoped to this component only */
    .generic-table-container .MuiTable-root {
      border-collapse: collapse;
      width: 100%;
    }

    /* Generic table header styles */
    .generic-table-header {
      background-color: ${DesignToken.brand.primary};
      color: ${DesignToken.brand.primaryContrast};
      font-weight: 600;
      border: 1px solid ${DesignToken.borderDefault};
      padding: ${spc.s}px;
      font-size: 14px;
      vertical-align: middle;
      line-height: 1.4;
    }

    /* Generic table cell styles */
    .generic-table-cell {
      border: 1px solid ${DesignToken.borderDefault};
      padding: ${spc.s}px;
      vertical-align: top;
      font-size: 14px;
      line-height: 1.4;
      background-color: ${DesignToken.surface1};
    }

    /* Ensure consistent border styling - scoped to this component only */
    .generic-table-container .MuiTableCell-root {
      border: 1px solid ${DesignToken.borderDefault};
    }

    /* Remove Material-UI default borders to prevent conflicts - scoped to this component only */
    .generic-table-container .MuiTableHead-root .MuiTableCell-root {
      border-bottom: 1px solid ${DesignToken.borderDefault};
    }

    .generic-table-container
      .MuiTableBody-root
      .MuiTableRow-root:not(:last-child)
      .MuiTableCell-root {
      border-bottom: 1px solid ${DesignToken.borderDefault};
    }

    /* Print styles for tables - scoped to this component only */
    @media print {
      .generic-table-container {
        box-shadow: none;
        border-radius: 0;
        margin: 8px 0;
      }

      /* Ensure consistent borders for both headers and cells in print - scoped to this component only */
      .generic-table-container .MuiTableCell-root {
        border: 1px solid ${DesignToken.textPrimary} !important;
        padding: 6px !important;
      }

      /* Remove any Material-UI default styling that might interfere - scoped to this component only */
      .generic-table-container .MuiTable-root {
        border-collapse: collapse !important;
      }

      .generic-table-container .MuiTableHead-root .MuiTableCell-root,
      .generic-table-container .MuiTableBody-root .MuiTableCell-root {
        border: 1px solid ${DesignToken.textPrimary} !important;
      }

      /* Ensure header background prints properly */
      .generic-table-header {
        background-color: ${DesignToken.brand.primary} !important;
        color: ${DesignToken.brand.primaryContrast} !important;
        font-weight: 600 !important;
      }

      /* Ensure cell background is white */
      .generic-table-cell {
        background-color: ${DesignToken.surface1} !important;
        color: ${DesignToken.textPrimary} !important;
      }
    }
  `
);
